import React, { useState, useEffect, useReducer, useCallback, useContext, useMemo, useRef } from 'react';

import { Box, ClickAwayListener } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import FormatListNumberedRoundedIcon from '@material-ui/icons/FormatListNumberedRounded';
// material ui assets
import ViewCarouselRoundedIcon from '@material-ui/icons/ViewCarouselRounded';
import Skeleton from '@material-ui/lab/Skeleton';
import { ScrollContext } from 'App';
import { ADD_FILTER, REMOVE_FILTER } from 'actions/actionTypes';
import { setTeamAction, resetAllAction, setFiltersAction } from 'actions/globalFilters';
import CreateGoalPanel from 'components/MeshList/goals/CreateGoalPanel';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import { TimePeriodDropDown } from 'components/ObjectiveTagList/TagsList';
import TreeView from 'components/TreeView';
import useLocalStorage from 'components/hooks/useLocalStorage';
// project data and models
import TeamModel from 'models/team';
// selectors
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
// helpers
import { getStartTSEndTSFromDateFilters } from 'screens/Team/TeamPageFilters/helper';
import GoalRecurrenceDetailsProvider from 'screens/admin/Objectives/Context/GoalRecurrenceDetailsProvider';
import GoalRecurrenceDetails from 'screens/admin/Objectives/GoalRecurrenceDetails';
import { MeshPopper, OKRMenuOptions } from 'screens/analytics/commonComponents';
import FilterBar from 'screens/analytics/goals/FilterBar';
import { GoalAnalyticsProvider, useGoalAnalytics } from 'screens/analytics/goals/GoalAnalyticsContext';
import GoalIndividual from 'screens/analytics/goals/GoalIndividual';
import ObjectiveTable from 'screens/analytics/goals/GoalObjective/ObjectiveTable';
import useObjectiveTablePagination from 'screens/analytics/goals/GoalObjective/hooks/useObjectiveTablePagination';
import { PopperProvider } from 'screens/analytics/popperContext';
// components
import SectionHeading from 'screens/meshIn/SectionHeading';
import { selectTeamPageGlobalFilters } from 'selectors/globalFilters';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
// styles
import {
  FlexContainer,
  StyledTypography,
  VerticallyCenterContainer,
  StyledButton,
  StyledIconButton,
  VerticallyAlignedFlexContainer,
} from 'styles/common';
import { checkModuleEnabled } from 'utils/helpers/moduleAccess';

import {
  TEAM_DURATION_VALUES,
  TEAM_TASKS_VIEW_TYPES,
  SEARCH_TAG_TYPES,
  TASK_TYPES_2,
  TEAM_TYPE,
  DEFAULT_ROWS_PER_PAGE,
  GOAL_ANALYTICS_VIEW_IDS,
} from 'constants/ProjectConstants';
import { getQueryParams, isTimeBoundPeriodValid } from 'constants/helper';
// assets
import { HierarchyIcon, PlusIcon3, StyledIcon } from 'constants/icons';
import { KH_LINKS } from 'constants/knowledgeHubLinks';
import { localStorageKeys } from 'constants/localStorageKeys';
// import logger from 'use-reducer-logger';
import {
  sendAppliedFilterEvent,
  sendClickedOnCreateKRevent,
  sendClickedOnTimePeriodEvent,
  sendViewTypeChangedEvent,
} from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { getTimeBoundTimePeriods } from 'services/admin';
import { getStartEndTimeFromDurationKey } from 'services/helpers/task';
import { showErrorSnackbar, showInfoSnackbar } from 'services/snackbar';
import { constructFiltersObject, getTeamTasksForListView, getTeamTasksStats } from 'services/task';

import TooltipInfo from 'uiToolkit/TooltipInfo';
import { Typography13 } from 'uiToolkit/Typography';

import CustomBackdrop from './CustomBackdrop';
import Header from './Header';
import Heading from './Header/Heading';
import RefreshButton from './SectionHeader/RefreshButton';
import Stats from './Stats';
import TeamPageFilters, {
  initialFiltersState,
  filtersReducersWithClearAll,
  RESET_ALL_FILTERS,
} from './TeamPageFilters';
import NewDueDateFilter from './TeamPageFilters/NewDueDateFilter';
import buildTeamPageFilters from './TeamPageFilters/buildTeamPageFilters';
import { getSelectedFiltersCountFromMap, dateParser } from './TeamPageFilters/helper';
import TeamTasksCardView from './TeamTasksCardView';
import TeamTasksListView from './TeamTasksListView';
import orderByReducer, { orderByInitialState } from './TeamTasksListView/orderByReducer';
import taskReducer, { ADD_TASKS } from './TeamTasksListView/taskReducer';
import ViewTypes, { Toggles } from './ViewTypes';
import { findAncestor, getEnabledTaskTypeMenuItemsForDefaultOption } from './helper';
import { Container } from './styles';

export function getTeamCountHeading(teamCount, viewType = '') {
  if (viewType === TEAM_TASKS_VIEW_TYPES.goalTree) return 'Goals Tree';
  if (teamCount <= 0) return 'Team Members';
  if (teamCount === 1) return '1 Team Member';
  return `${teamCount} Team Members`;
}

const UPDATE_TASKS_DATA_CHANGE = 'UPDATE_TASKS_DATA_CHANGE';
const UPDATE_TASKS_RELOAD = 'UPDATE_TASKS_RELOAD';

function tasksDataChangeReducer(state, action) {
  switch (action.type) {
    case UPDATE_TASKS_DATA_CHANGE:
      return { ...state, [action.data.type]: action.data.value };
    // case UPDATE_TASKS_RELOAD:
    //   const newState = { ...state };
    //   newState.reload[action.data.type] = action.data.value;
    //   return newState;
    default:
      return state;
  }
}

function useScrollToTopState(pathname) {
  const { current: scrollParentRef } = useContext(ScrollContext) ?? {};

  useEffect(() => {
    // when you come on team page, make sure that you scroll to the top
    if (scrollParentRef) {
      scrollParentRef.scrollTo(0, 0);
    }
  }, [pathname, scrollParentRef]);
}

export function useViewTypeState(selectedTaskType, sideNavSelectedCohort, isGoalOptionSelected) {
  const localStorageViewType = getLocalStorageViewType(selectedTaskType);
  let defaultInitialView = localStorageViewType;
  if (isGoalOptionSelected && sideNavSelectedCohort?.type === 'CUSTOM_TYPE') {
    defaultInitialView = TEAM_TASKS_VIEW_TYPES.list;
    setLocalStorageViewType(selectedTaskType, defaultInitialView);
  }
  const [teamTasksViewType, setTeamTasksViewType] = useState(
    defaultInitialView ?? getDefaultViewType(selectedTaskType)
  );

  function handleTeamViewTypeChange(event, viewType) {
    if (!viewType) return;
    sendViewTypeChangedEvent({
      eventProperties: {
        type: viewType,
        source: selectedTaskType,
      },
    });

    setTeamTasksViewType(viewType);
    setLocalStorageViewType(selectedTaskType, viewType);
  }

  useEffect(() => {
    if (sideNavSelectedCohort?.type === 'CUSTOM_TYPE') {
      setTeamTasksViewType(TEAM_TASKS_VIEW_TYPES.list);
    }
  }, [sideNavSelectedCohort?.type]);
  return { teamTasksViewType, setTeamTasksViewType, handleTeamViewTypeChange };
}

export const getLocalStorageFilters = (type) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsFilters : localStorageKeys.teamPageTasksFilters;
  const val = localStorage.getItem(key);
  return (val && JSON.parse(val, dateParser)) || null;
};

export const setLocalStorageFilters = (type, filters) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsFilters : localStorageKeys.teamPageTasksFilters;
  localStorage.setItem(key, JSON.stringify(filters));
};

export const getLocalStorageViewType = (type) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsViewType : localStorageKeys.teamPageTasksViewType;
  const val = localStorage.getItem(key);
  return val || null;
};

export const setLocalStorageViewType = (type, viewType) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsViewType : localStorageKeys.teamPageTasksViewType;
  localStorage.setItem(key, viewType);
};

export const getLocalStorageStatsFilters = (type) => {
  const key =
    type === TASK_TYPES_2.goal
      ? localStorageKeys.teamPageGoalsStatsFilters
      : localStorageKeys.teamPageTasksStatsFilters;
  const val = localStorage.getItem(key);

  return (val && JSON.parse(val)) || null;
};

const defaultGoalStats = [
  {
    type: 'STATUS',
    value: 'ON_TRACK',
    displayValue: 'On-Track',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'OFF_TRACK',
    displayValue: 'Off-Track',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'AT_RISK',
    displayValue: 'At-Risk',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'CLOSED',
    displayValue: 'CLOSED',
    operator: 'IN',
  },
];

export const defaultTaskStats = [
  {
    type: 'STATUS',
    value: 'ONGOING',
    displayValue: 'On-going',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'DELAYED',
    displayValue: 'Delayed',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'UNPLANNED',
    displayValue: 'Unplanned',
    operator: 'IN',
  },
];

export const getDefaultLocalStorageStatsFilters = (type) => {
  const val = getLocalStorageStatsFilters(type);
  if (val) {
    return val;
  }

  if (type === TASK_TYPES_2.goal) {
    setLocalStorageStatsFilters(type, defaultGoalStats);
    return defaultGoalStats;
  }
  setLocalStorageStatsFilters(type, defaultTaskStats);
  return defaultTaskStats;
};

export const setLocalStorageStatsFilters = (type, filters) => {
  const key =
    type === TASK_TYPES_2.goal
      ? localStorageKeys.teamPageGoalsStatsFilters
      : localStorageKeys.teamPageTasksStatsFilters;
  localStorage.setItem(key, JSON.stringify(filters));
};

export function useTeamPageState({ sideNavSelectedCohort }) {
  const location = useLocation();
  const [teamCount, setTeamCount] = useState(0);
  // const [performanceStats, setPerformanceStats] = useState({ reliabilityScore: 0, growth: 0 });
  const [team, setTeam] = useState(null);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const personalTeam = loggedInEmployee.teams.filter((team) => team.teamType === TEAM_TYPE.personal)[0];
  const durationMenuItems = TEAM_DURATION_VALUES;
  const [selectedDurationId, setSelectedDurationValue] = useState('LASTYEAR');
  let selectedTaskTypeFromLs = localStorage.getItem(localStorageKeys.selectedTaskType);
  if (selectedTaskTypeFromLs && !checkModuleEnabled(selectedTaskTypeFromLs)) {
    // module not enabled, so not valid anymore
    selectedTaskTypeFromLs = null;
    localStorage.removeItem(localStorageKeys.selectedTaskType);
  }
  const [selectedTaskType, setSelectedTaskType] = useState(
    selectedTaskTypeFromLs || getEnabledTaskTypeMenuItemsForDefaultOption()[0].value
  );
  const isGoalOptionSelected = selectedTaskType === TASK_TYPES_2.goal;
  const { teamTasksViewType, setTeamTasksViewType, handleTeamViewTypeChange } = useViewTypeState(
    selectedTaskType,
    sideNavSelectedCohort,
    isGoalOptionSelected
  );

  const dispatch = useDispatch();
  const [tasksLoading, updateTasksLoading] = useState(false);
  const activeFilters = useSelector(selectTeamPageGlobalFilters, shallowEqual).filters;
  // will be used in Stats when treeview is selected
  const activeFiltersWithoutObjectiveFilter = activeFilters.filter((item) => item.type !== 'TIMEBOUND_OBJECTIVE');

  // const [activeFilters, activeFiltersDispatch] = useReducer(filtersReducer, []);
  const [tasksDataChanged, tasksDataChangeDispatch] = useReducer(tasksDataChangeReducer, {
    [TEAM_TASKS_VIEW_TYPES.card]: false,
    [TEAM_TASKS_VIEW_TYPES.list]: false,
  });
  useScrollToTopState(location.pathname);
  const [taskTags, setTaskTags] = useState(null);
  const [goalTags, setGoalTags] = useState(null);
  const [refreshData, setRefreshData] = useState(false);
  const addStatsFilter = (type, value, displayValue, operator) => {
    sendAppliedFilterEvent({
      eventProperties: {
        filter: displayValue,
        source: selectedTaskType,
      },
    });
    dispatch({
      type: ADD_FILTER,
      data: {
        type,
        value,
        displayValue,
        operator: operator || 'IN',
      },
    });
    // save to local storage too
    const currentStatsFilters = getLocalStorageStatsFilters(selectedTaskType) || [];
    setLocalStorageStatsFilters(selectedTaskType, [
      ...currentStatsFilters,
      {
        type,
        value,
        displayValue,
        operator: operator || 'IN',
      },
    ]);
  };

  const updateIsTasksDataChanged = useCallback((type, value) => {
    tasksDataChangeDispatch({ type: UPDATE_TASKS_DATA_CHANGE, data: { type, value } });
  }, []);
  const updateIsCardViewDataChanged = useCallback(
    (value) => {
      updateIsTasksDataChanged(TEAM_TASKS_VIEW_TYPES.card, value);
    },
    [updateIsTasksDataChanged]
  );

  const removeStatsFilter = (type, value, displayValue, operator) => {
    sendAppliedFilterEvent({
      eventProperties: {
        filter: displayValue,
        source: selectedTaskType,
      },
    });
    dispatch({
      type: REMOVE_FILTER,
      data: {
        type,
        value,
        operator: operator || 'IN',
      },
    });
    let currentStatsFilters = getLocalStorageStatsFilters(selectedTaskType) || [];
    currentStatsFilters = currentStatsFilters.filter((item) => item.value !== value);
    setLocalStorageStatsFilters(selectedTaskType, [...currentStatsFilters]);
  };
  const [newFiltersState, newFiltersDispatch] = useReducer(
    // logger(filtersReducersWithClearAll),
    filtersReducersWithClearAll,
    initialFiltersState
  );

  useEffect(() => {
    const localStorageFilters = getLocalStorageFilters(selectedTaskType);
    const filters = localStorageFilters || initialFiltersState;
    // removing initial state of type = "TASKS" when GOALS is selectedTaskType
    if (isGoalOptionSelected) {
      filters.taskTypeFilters = {};
    }

    newFiltersDispatch({ type: RESET_ALL_FILTERS, data: filters });
  }, [selectedTaskType]);

  return {
    teamCount,
    setTeamCount,
    // performanceStats,
    // setPerformanceStats,
    team,
    setTeam,
    personalTeam,
    selectedDurationId,
    setSelectedDurationValue,
    setSelectedTaskType,
    isGoalOptionSelected,
    teamTasksViewType,
    dispatch,
    tasksLoading,
    updateTasksLoading,
    activeFilters,
    activeFiltersWithoutObjectiveFilter,
    tasksDataChanged,
    taskTags,
    setTaskTags,
    goalTags,
    setGoalTags,
    refreshData,
    setRefreshData,
    setTeamTasksViewType,
    addStatsFilter,
    updateIsTasksDataChanged,
    updateIsCardViewDataChanged,
    removeStatsFilter,
    handleTeamViewTypeChange,
    selectedTaskType,
    loggedInEmployee,
    durationMenuItems,
    activeFiltersDispatch: dispatch,
    newFiltersState,
    newFiltersDispatch,
  };
}

export function getDefaultViewType(selectedTaskType) {
  return selectedTaskType === TASK_TYPES_2.goal ? TEAM_TASKS_VIEW_TYPES.list : TEAM_TASKS_VIEW_TYPES.card;
}

function getCompletedOnDurationFromFilters(newFiltersState) {
  const selectedCompletedFiltersCount = getSelectedFiltersCountFromMap(newFiltersState.completedOnFilters);
  return selectedCompletedFiltersCount > 0
    ? getStartTSEndTSFromDateFilters(newFiltersState.completedOnFilters)
    : getStartEndTimeFromDurationKey('LASTYEAR');
}

const getTimeBoundsFromLS = () => {
  try {
    const timeBounds = JSON.parse(window.localStorage.getItem(localStorageKeys.teamPageGoalsTimeBounds));
    return timeBounds || [];
  } catch (e) {
    console.error('Error in reading LS: ', e);
  }
};

export const setTimeBoundsToLS = (timeBounds) => {
  try {
    window.localStorage.setItem(localStorageKeys.teamPageGoalsTimeBounds, JSON.stringify(timeBounds));
  } catch (e) {
    console.error('Error in saving to LS: ', e);
  }
};

const ViewTypeSelection = ({ isGoalOptionSelected, sideNavSelectedCohort }) => {
  const hideGoalTreeCardView = isGoalOptionSelected && sideNavSelectedCohort?.type !== 'POD';

  return (
    <ViewTypes.Toggles hideOnlyExistingToggle={hideGoalTreeCardView}>
      <Toggles.Toggle
        key={1}
        value={TEAM_TASKS_VIEW_TYPES.card}
        icon={
          <StyledIcon size="tiny">
            <ViewCarouselRoundedIcon />
          </StyledIcon>
        }
        disabled={hideGoalTreeCardView}
      />

      <Toggles.Toggle
        key={2}
        value={TEAM_TASKS_VIEW_TYPES.list}
        icon={
          <StyledIcon size="tiny">
            <FormatListNumberedRoundedIcon />
          </StyledIcon>
        }
        isLast={!isGoalOptionSelected}
      />
      {isGoalOptionSelected && (
        <Toggles.Toggle
          key={3}
          value={TEAM_TASKS_VIEW_TYPES.goalTree}
          icon={
            <StyledIcon size="tiny">
              <HierarchyIcon />
            </StyledIcon>
          }
          isLast={isGoalOptionSelected}
          disabled={hideGoalTreeCardView}
        />
      )}
    </ViewTypes.Toggles>
  );
};

function TeamWithoutGoalAnalyticsProvider() {
  const location = useLocation();
  const queryString = location.search;
  const { teamId: paramsTeamId } = useParams();
  const dispatch = useDispatch();

  const sideNavSelectedCohort = useSelectedView();

  const {
    teamCount,
    setTeamCount,
    team,
    setTeam,
    personalTeam,
    setSelectedDurationValue,
    setSelectedTaskType,
    isGoalOptionSelected,
    teamTasksViewType,
    tasksLoading,
    updateTasksLoading,
    activeFilters,
    activeFiltersWithoutObjectiveFilter,
    tasksDataChanged,
    setTaskTags,
    setGoalTags,
    setTeamTasksViewType,
    refreshData,
    setRefreshData,
    addStatsFilter,
    updateIsTasksDataChanged,
    updateIsCardViewDataChanged,
    removeStatsFilter,
    // resetGlobalApiFilters,
    handleTeamViewTypeChange,
    selectedTaskType,
    loggedInEmployee,
    activeFiltersDispatch,
    newFiltersState,
    newFiltersDispatch,
  } = useTeamPageState({ sideNavSelectedCohort });

  const { isGoalsPage, isInitiativesPage } = getQueryParams(queryString);

  const [duration, setDuration] = useState(getCompletedOnDurationFromFilters(newFiltersState));
  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);
  const [isOpenCreateGoalTreeViewPanel, setIsOpenCreateGoalTreeViewPanel] = useState(false);
  const [isCreateNewTaskInProgress, setIsCreateNewTaskInProgress] = useState(false);

  // had to keep new filter logic separate from the old, so that it works on mobile as well
  // TODO: remove old filter logic and only use this new one
  useEffect(() => {
    const newActiveFilters = buildTeamPageFilters(newFiltersState, isGoalOptionSelected);
    setDuration((duration) => {
      const newDuration = getCompletedOnDurationFromFilters(newFiltersState);
      return JSON.stringify(duration) !== JSON.stringify(newDuration) ? newDuration : duration;
    });
    const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(selectedTaskType) || [];
    activeFiltersDispatch(setFiltersAction([...newActiveFilters, ...localStorageStatsFilters]));
  }, [JSON.stringify(newFiltersState)]);

  const updateIsTableDataChanged = useCallback(
    (value) => {
      updateIsTasksDataChanged(TEAM_TASKS_VIEW_TYPES.list, value);
    },
    [updateIsTasksDataChanged]
  );

  useEffect(() => {
    const teamId = paramsTeamId;
    if (!teamId) return;
    const { teamName } = getQueryParams(queryString);

    dispatch(setTeamAction(teamId)).then(() => {
      setTeam(new TeamModel({ id: teamId, name: teamName }));
    });
  }, [paramsTeamId, isGoalsPage, isInitiativesPage]);

  useEffect(
    () => () => {
      dispatch(resetAllAction());
    },
    []
  );

  useEffect(() => {
    const { employeeFilters, ...otherTaskFilters } = newFiltersState;
    const { primaryPodFilters, objectiveFilters, ...otherGoalFilters } = otherTaskFilters;

    if (selectedTaskType === 'TASK') {
      setLocalStorageFilters(selectedTaskType, otherTaskFilters);
    } else {
      setLocalStorageFilters(selectedTaskType, otherGoalFilters);
    }
  }, [selectedTaskType, newFiltersState]);

  // const onTaskTypeChange = (e, value) => {
  //   if (value) localStorage.setItem(localStorageKeys.selectedTaskType, value);
  //   setSelectedTaskType(value || getEnabledTaskTypeMenuItemsForDefaultOption()[0].value);

  //   const teamTasksViewType = getLocalStorageViewType(value) || getDefaultViewType(value);
  //   setTeamTasksViewType(teamTasksViewType);

  //   sendTeamHubEvents({
  //     eventName: TEAM_HUB_EVENTS.toggledTaskType,
  //     page: {
  //       pageName: routesForTracking.team.pageName,
  //       flow: 'Activity'
  //     },
  //     taskType: value,
  //     view: teamTasksViewType
  //   });

  //   const localStorageFilters = getLocalStorageFilters(value);
  //   const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(value) || [];
  //   if (localStorageFilters) {
  //     dispatch(
  //       setFiltersAction([
  //         ...buildTeamPageFilters(localStorageFilters, value === TASK_TYPES_2['goal']),
  //         ...localStorageStatsFilters
  //       ])
  //     );
  //   } else {
  //     dispatch(setFiltersAction([...localStorageStatsFilters]));
  //   }
  // };

  const onTaskTypeSetForGoalsOrInitiativePage = (value) => {
    setSelectedTaskType(value);

    const teamTasksViewType = getDefaultViewType(value);
    if (value !== TASK_TYPES_2.goal) {
      setTeamTasksViewType(teamTasksViewType);
    }
  };

  useEffect(() => {
    if (isGoalsPage) {
      onTaskTypeSetForGoalsOrInitiativePage(TASK_TYPES_2.goal);
    }
    if (isInitiativesPage) {
      onTaskTypeSetForGoalsOrInitiativePage(TASK_TYPES_2.task);
    }
  }, [isGoalsPage, isInitiativesPage]);

  // sorting logic
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const [totalTasks, setTotalTasks] = useState(0);
  const [tasks, tasksDispatch] = useReducer(taskReducer, []);
  const [goals, goalsDispatch] = useReducer(taskReducer, []);
  const [selectedTimeBoundPeriods, setSelectedTimeBoundPeriods] = useState([]);
  const [timePeriodData, setTimePeriodData] = useState([]);
  const [allTimePeriodsList, setAllTimePeriodsList] = useState([]);

  const [{ field: orderBy, type: order }, orderByDispatch] = useReducer(orderByReducer, orderByInitialState);

  // add button in revamped goal menu options state
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const anchorRef = useRef();

  // objective panel state
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);

  // getdata for sorting

  const getTimePeriodsData = () => {
    getTimeBoundTimePeriods()
      .then((timePeriodData) => {
        const { previous, current, next, other } = timePeriodData;
        const allTimePeriods = [previous, current, next, ...other].filter((item) => item !== null);
        setTimePeriodData(timePeriodData);
        setAllTimePeriodsList(allTimePeriods);
        const timeBoundPeriodsFromLS = getTimeBoundsFromLS();
        // read from LS and check if it's still valid from latest fetched timeBound list
        if (
          Array.isArray(timeBoundPeriodsFromLS) &&
          timeBoundPeriodsFromLS.length > 0 &&
          isTimeBoundPeriodValid(allTimePeriods, timeBoundPeriodsFromLS[0])
        ) {
          setSelectedTimeBoundPeriods(timeBoundPeriodsFromLS);
          updateTimeBoundPeriods(timeBoundPeriodsFromLS);
        } else {
          setSelectedTimeBoundPeriods([current]);
          updateTimeBoundPeriods([current]);
          setTimeBoundsToLS([current]);
        }
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message)));
  };

  useEffect(() => {
    getTimePeriodsData();
  }, []);

  const getData = (resetPage) => {
    if (loading || !sideNavSelectedCohort) {
      return;
    }
    setLoading(true);
    updateTasksLoading(true);
    if (resetPage) {
      setPage(0);
    }

    getTeamTasksStats({
      tagId: team.id,
      tagType: SEARCH_TAG_TYPES.team,
      // durationKey,
      startTS: duration.startTS,
      endTS: duration.endTS,
      filters: activeFilters,
      type: selectedTaskType,
      timeBounds: selectedTimeBoundPeriods,
      selectedCohort: sideNavSelectedCohort,
    })
      .then((res) => {
        setTotalTasks(res.entity.count);
        getTasks(resetPage);
      })
      .catch((err) => {
        if (selectedTaskType === TASK_TYPES_2.task) {
          tasksDispatch({ type: ADD_TASKS, data: { tasks: [] } });
        } else {
          goalsDispatch({ type: ADD_TASKS, data: { tasks: [] } });
        }
        setLoading(false);
        updateTasksLoading(false);
      });
  };

  const getTasks = (resetPage) => {
    setLoading(true);
    updateTasksLoading(true);
    getTeamTasksForListView({
      tagId: team.name === 'My Initiatives' ? -1 : team.id,
      tagType: SEARCH_TAG_TYPES.team,
      // durationKey,
      startTS: duration.startTS,
      endTS: duration.endTS,
      limit: rowsPerPage,
      offset: resetPage ? 0 : page * rowsPerPage,
      orderByParam: orderBy,
      order,
      filters:
        team.name === 'My Initiatives'
          ? [
              ...activeFilters,
              {
                type: 'NAME',
                value: loggedInEmployee.id,
                operator: 'IN',
              },
            ]
          : activeFilters,
      type: selectedTaskType,
      timeBoundPeriod: selectedTaskType === TASK_TYPES_2.goal ? (selectedTimeBoundPeriods?.[0] ?? null) : null,
      selectedCohort: sideNavSelectedCohort,
    })
      .then((tasks) => {
        if (selectedTaskType === TASK_TYPES_2.task) {
          tasksDispatch({ type: ADD_TASKS, data: { tasks } });
        } else {
          goalsDispatch({ type: ADD_TASKS, data: { tasks } });
        }
      })
      .catch((err) => {
        if (selectedTaskType === TASK_TYPES_2.task) {
          tasksDispatch({ type: ADD_TASKS, data: { tasks: [] } });
        } else {
          goalsDispatch({ type: ADD_TASKS, data: { tasks: [] } });
        }
      })
      .finally(() => {
        updateTasksLoading(false);
        setLoading(false);
      });
  };

  const cardViewStyles = useMemo(
    () => ({
      display: teamTasksViewType === TEAM_TASKS_VIEW_TYPES.card ? 'flex' : 'none',
    }),
    [teamTasksViewType]
  );
  const listviewStyles = useMemo(
    () => ({
      display: teamTasksViewType === TEAM_TASKS_VIEW_TYPES.list ? 'block' : 'none',
    }),
    [teamTasksViewType]
  );

  const addNewGoal = () => {
    if (teamTasksViewType === TEAM_TASKS_VIEW_TYPES.list) {
      setIsOpenCreateGoalPanel(true);
    } else if (teamTasksViewType === TEAM_TASKS_VIEW_TYPES.goalTree) {
      setIsOpenCreateGoalTreeViewPanel(true);
    } else {
      const currentUserCreateGoalButton = document.getElementsByClassName(`create-goal-${loggedInEmployee.id}`)[0];
      // logged in user mesh list is not shown
      if (!currentUserCreateGoalButton) {
        dispatch(
          showInfoSnackbar(
            `Your goals list is not shown as per applied filters. To create a goal for other person, use the Create New button on their list.`,
            4000
          )
        );
        return;
      }
      const meshlistAncestor = findAncestor(currentUserCreateGoalButton, 'meshlist');
      meshlistAncestor.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
      currentUserCreateGoalButton.click();
    }
  };

  const addNewTask = () => {
    if (teamTasksViewType === TEAM_TASKS_VIEW_TYPES.list) {
      setIsCreateNewTaskInProgress(true);
    } else {
      const currentUserCreateTaskButton = document.getElementsByClassName(`create-task-${loggedInEmployee.id}`)[0];
      // logged in user mesh list is not shown
      if (!currentUserCreateTaskButton) {
        dispatch(
          showInfoSnackbar(
            `Your tasks list is not shown as per applied filters. To create a task for other person, use the Create New button on their list.`,
            4000
          )
        );
        return;
      }
      const meshlistAncestor = findAncestor(currentUserCreateTaskButton, 'meshlist');
      meshlistAncestor.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
      currentUserCreateTaskButton.click();
    }
  };

  const refresh = () => {
    setRefreshData(true);
    setTimeout(() => {
      setRefreshData(false);
    }, 100);
  };

  useEffect(() => {
    refresh();
  }, [selectedTimeBoundPeriods]);

  const showListView = useCallback(() => {
    setTeamTasksViewType(TEAM_TASKS_VIEW_TYPES.list);
  }, [setTeamTasksViewType]);

  const memoizedFiltersState = useMemo(() => newFiltersState, [newFiltersState]);

  // objective table states
  const objectiveTableRowsPerPage = 'objectiveTableRowsPerPage';

  const [rowsPerPageLS, setRowsPerPageLS] = useLocalStorage(localStorageKeys.analyticsGoalTableFilters);

  const {
    data,
    dataCount,
    rowsPerPageOptions,
    rowsPerPage: rowsPerPageObjectiveTable,
    pageNumber,
    setPageNumber,
    setRowsPerPage: setRowsPerPageObjectiveTable,
    setData,
    setAllData,
    updateRowDataModifiedForArray,
    addNewObjective,
  } = useObjectiveTablePagination({
    rowsPerPageOptions: [5, 10, 15],
    defaultRowsPerPage: rowsPerPageLS[objectiveTableRowsPerPage],
    initialPage: 0,
  });

  // timebound details of newly created objective
  const [timeboundDetailsOfCreatedObjective, setTimeboundDetailsOfCreatedObjective] = useState(null);

  const {
    state: {
      selectedView,
      currentState: { noData, errorData },
    },
    updateTimeBoundPeriods,
  } = useGoalAnalytics();
  const { viewId } = selectedView;

  const [isGoalPanelOfCreatedObjective, setIsGoalPanelOfCreatedObjective] = useState(false);

  const showAnalyticsIndividualTable = false;

  const myTeamId = useMemo(() => {
    if (Array.isArray(loggedInEmployee.teams)) {
      const myTeam = loggedInEmployee.teams.find((team) => team.teamType === 'HRIS' && team.userRole === 'MANAGER');
      return myTeam?.id;
    }
  }, [loggedInEmployee.teams]);

  if (!team || !team?.id) {
    return <Skeleton height="30vh" />;
  }

  if (!sideNavSelectedCohort) {
    return <Skeleton height="30vh" />;
  }

  const finalLink = isInitiativesPage ? KH_LINKS.TEAMHUB_VIEW_2.url : KH_LINKS.TEAMHUB_VIEW_GOALS.url;

  return (
    team &&
    team.id && (
      <Container style={{ height: 'auto' }} className="teamPageContainer">
        {/* <MainContent> */}
        <Header>
          <HeadingWrapper>
            <Heading heading={team.name} />
            <TooltipInfo
              title={
                isInitiativesPage
                  ? KH_LINKS.TEAMHUB_VIEW_TASKS.description
                  : isGoalsPage
                    ? KH_LINKS.TEAMHUB_VIEW_GOALS.description
                    : KH_LINKS.TEAMHUB_VIEW_2.description
              }
              linkUrl={finalLink}
              leftGutter={2}
            />
          </HeadingWrapper>
          <FlexContainer>
            {/* <StyledIcon size="medium" color={theme.palette.error.main}>
<NotificationSettingsIcon />
</StyledIcon>

<StyledIcon size="medium" color={theme.palette.error.main}>
<NotificationTiltedIcon />
</StyledIcon> */}
            {myTeamId === team.id && isGoalsPage ? (
              <Box style={{ padding: '0 8px', display: 'flex', cursor: 'pointer' }}>
                {/* <DownChevronSvgIcon color="#ccc" size="fixed20px" /> */}
                <GoalRecurrenceDetailsProvider teamId={myTeamId}>
                  <GoalRecurrenceDetails isIcon teamId={myTeamId} isDisabled={false} />
                </GoalRecurrenceDetailsProvider>
              </Box>
            ) : null}

            {isGoalOptionSelected ? (
              allTimePeriodsList.length > 0 && selectedTimeBoundPeriods.length > 0 ? (
                // later when requirement comes, this component will be able to select and render multiple time periods
                <TimePeriodDropDown
                  timePeriodData={timePeriodData}
                  selectedTimeBoundPeriod={selectedTimeBoundPeriods?.[0] ?? null}
                  setSelectedTimeBoundPeriod={(timePeriod) => {
                    sendClickedOnTimePeriodEvent({
                      eventProperties: {
                        source: 'Goals Pod',
                      },
                    });
                    setSelectedTimeBoundPeriods([timePeriod]);
                    updateTimeBoundPeriods([timePeriod]);

                    setTimeBoundsToLS([timePeriod]);
                  }}
                />
              ) : null
            ) : (
              <NewDueDateFilter
                selectedFiltersDispatch={newFiltersDispatch}
                selectedFilters={newFiltersState.dueDateFilters}
                width="18%"
                iconSize="fixed12px"
              />
            )}
            {/* {!(isGoalsPage || isInitiativesPage) && (
<TaskGoalToggle
  selectedTaskType={selectedTaskType}
  onTaskTypeChange={onTaskTypeChange}
/>
)} */}
          </FlexContainer>
        </Header>
        {!isGoalOptionSelected || (isGoalOptionSelected && selectedTimeBoundPeriods.length !== 0) ? (
          <>
            <TeamPageFilters
              teamId={team.id}
              filtersDispatch={activeFiltersDispatch}
              newFiltersState={memoizedFiltersState}
              newFiltersDispatch={newFiltersDispatch}
              selectedTaskType={selectedTaskType}
              orderByDispatch={orderByDispatch}
              orderBy={orderBy}
              order={order}
              teamTasksViewType={teamTasksViewType}
              selectedTimeBoundPeriod={selectedTimeBoundPeriods?.[0] ?? null}
            />
            <Stats
              tagId={team.id}
              tagType={SEARCH_TAG_TYPES.team}
              type={selectedTaskType}
              activeFilters={
                TEAM_TASKS_VIEW_TYPES.goalTree === teamTasksViewType
                  ? activeFiltersWithoutObjectiveFilter
                  : activeFilters
              }
              addFilter={addStatsFilter}
              {...duration}
              removeFilter={removeStatsFilter}
              tasksLoading={tasksLoading}
              refreshData={refreshData}
              timeBoundPeriod={selectedTimeBoundPeriods?.[0] ?? null}
              sideNavSelectedCohort={sideNavSelectedCohort}
            />
            <SectionHeading
              HeadingComponent={
                <VerticallyCenterContainer>
                  <SectionHeading.Heading
                    // display="inline"
                    // variant="h6"
                    // color="textPrimary"
                    // weight={600}
                    heading={getTeamCountHeading(teamCount, teamTasksViewType)}
                  />
                  <RefreshButton onClick={refresh} isDisabled={tasksLoading} />
                </VerticallyCenterContainer>
              }
              actionComponent={
                <ViewTypes teamTasksViewType={teamTasksViewType} handleTeamViewTypeChange={handleTeamViewTypeChange}>
                  {selectedTaskType === TASK_TYPES_2.goal ? (
                    <>
                      <ClickAwayListener onClickAway={() => setMenuIsOpen(false)}>
                        <StyledButton
                          variant="outlined"
                          borderColor="primary"
                          onClick={() => setMenuIsOpen((prev) => !prev)}
                          customColor="white"
                          style={{ marginRight: '1rem' }}
                          ref={anchorRef}
                        >
                          <VerticallyAlignedFlexContainer>
                            <ColoredIconButton style={{ marginRight: '0.5rem' }}>
                              <PlusIcon3 size="fixed10px" color="blue" />
                            </ColoredIconButton>
                            <Typography13 customColor="primary">Add New</Typography13>
                            <StyledIcon
                              size="header
                "
                              color="primary"
                            >
                              <ArrowDropDownIcon />
                            </StyledIcon>
                          </VerticallyAlignedFlexContainer>
                        </StyledButton>
                      </ClickAwayListener>
                      <OKRMenuOptions
                        addKR={() => {
                          sendClickedOnCreateKRevent({
                            eventProperties: {
                              source: 'Team Page',
                            },
                          });
                          setIsOpenCreateGoalPanel(true);
                        }}
                        addObjective={() => setIsOpenCreateObjectivePanel(true)}
                        anchorEl={anchorRef}
                        isOpen={menuIsOpen}
                        setIsOpen={setMenuIsOpen}
                      />
                    </>
                  ) : (
                    <CreateButton
                      onClick={isGoalOptionSelected ? addNewGoal : addNewTask}
                      customColor="white"
                      fontColor="primary"
                      variant="outlined"
                    >
                      <StyledTypography weight={600} variant="body2" lineheight="24px" texttransform="capitalize">
                        Create {isGoalOptionSelected ? 'goal' : 'Initiative'}
                      </StyledTypography>
                    </CreateButton>
                  )}

                  <CreateGoalPanel
                    open={isOpenCreateGoalPanel}
                    onCancel={() => {
                      setIsOpenCreateGoalPanel(false);
                    }}
                    goalListDispatch={tasksDispatch}
                    employee={loggedInEmployee}
                    // defaultTask={defaultCreateTask}
                    subObjective={isGoalPanelOfCreatedObjective ? { ...timeboundDetailsOfCreatedObjective } : null}
                    source="teampage"
                    eventSource="Team Page"
                    selectedTimeBoundPeriod={selectedTimeBoundPeriods?.[0] ?? null}
                    afterGoalCreation={refresh}
                  />

                  {isOpenCreateObjectivePanel && (
                    <ObjectiveSidePanel
                      open={isOpenCreateObjectivePanel}
                      onCancel={() => {
                        setTimeboundDetailsOfCreatedObjective(null);
                        setIsGoalPanelOfCreatedObjective(false);
                        setIsOpenCreateObjectivePanel(false);
                      }}
                      selectedTimeBoundPeriod={selectedTimeBoundPeriods?.[0] ?? null}
                      isAlignmentAtParentLevel
                      setIsOpenCreateGoalPanel={(value) => {
                        setIsOpenCreateGoalPanel(value);
                        setIsGoalPanelOfCreatedObjective(value);
                      }}
                      setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
                      source="Team Page"
                    />
                  )}

                  {/* <ViewTypes.ViewTypeDisplay /> */}
                  <ViewTypeSelection
                    isGoalOptionSelected={isGoalOptionSelected}
                    sideNavSelectedCohort={sideNavSelectedCohort}
                  />
                </ViewTypes>
              }
            />

            {isGoalsPage && showAnalyticsIndividualTable && (
              <FilterBar
              // isDisabled={Boolean(expandedRowDetails?.headerComponent)}
              // isDisabled={false}
              />
            )}

            {!noData &&
              !errorData &&
              viewId === GOAL_ANALYTICS_VIEW_IDS.INDIVIDUAL &&
              teamTasksViewType === TEAM_TASKS_VIEW_TYPES.list &&
              showAnalyticsIndividualTable && <GoalIndividual isWithGraphs={false} isVerV1 />}

            {viewId !== GOAL_ANALYTICS_VIEW_IDS.INDIVIDUAL && (
              <ViewsContainer>
                {TEAM_TASKS_VIEW_TYPES.goalTree === teamTasksViewType && (
                  <TreeView
                    // getInitialData={getObjectives.bind(null, { parentTagId: null })}
                    // getChildrenData={parentTagId =>
                    //   loadChildrenData(
                    //     getObjectives.bind(null, { parentTagId }),
                    //     getGoalsForTeam(team.id).bind(null, buildFiltersForTeam(parentTagId))
                    //   )
                    // }
                    isOpenCreateGoalPanel={isOpenCreateGoalTreeViewPanel}
                    setIsOpenCreateGoalPanel={setIsOpenCreateGoalTreeViewPanel}
                    employee={loggedInEmployee}
                    // buildInitialTreeData={buildObjectivesData(getDisplayNameForTeam)}
                    selectedTimeBoundPeriod={selectedTimeBoundPeriods?.[0] ?? null}
                    refreshData={refreshData}
                    refreshFn={refresh}
                    teamId={team.id}
                    isOpenCreateObjectivePanel={isOpenCreateObjectivePanel}
                    setIsOpenCreateObjectivePanel={setIsOpenCreateObjectivePanel}
                    selectedCohort={sideNavSelectedCohort}
                  />
                )}

                {/* <TeamViewTypesContainer></TeamViewTypesContainer> */}

                {selectedTaskType === TASK_TYPES_2.goal && TEAM_TASKS_VIEW_TYPES.list === teamTasksViewType ? (
                  <PopperProvider>
                    <ObjectiveTable
                      isTeamHubView
                      selectedTimeBoundPeriodsTeamHub={selectedTimeBoundPeriods?.[0] ?? null}
                      teamHubFilters={constructFiltersObject(activeFilters, TASK_TYPES_2.goal)}
                      tagId={team.id}
                      tagType={SEARCH_TAG_TYPES.team}
                      isOpenCreateObjectivePanel={isOpenCreateObjectivePanel}
                      setIsOpenCreateObjectivePanel={setIsOpenCreateObjectivePanel}
                      loadingTeamHub={loading}
                      activeFilters={activeFilters}
                      refreshData={refreshData}
                      paginationProps={{
                        data,
                        dataCount,
                        rowsPerPageOptions,
                        rowsPerPage: rowsPerPageObjectiveTable,
                        pageNumber,
                        setPageNumber,
                        setRowsPerPage: setRowsPerPageObjectiveTable,
                        setData,
                        setAllData,
                        updateRowDataModifiedForArray,
                        addNewObjective,
                      }}
                      teamPageSelectedCohort={sideNavSelectedCohort}
                    />
                    <MeshPopper />
                  </PopperProvider>
                ) : (
                  <TeamTasksListView
                    tagId={team.id}
                    tagType={SEARCH_TAG_TYPES.team}
                    style={listviewStyles}
                    activeFilters={activeFilters}
                    isSectionVisible={TEAM_TASKS_VIEW_TYPES.list === teamTasksViewType}
                    updateTasksLoading={updateTasksLoading}
                    updateIsTasksDataChanged={updateIsTableDataChanged}
                    hasCardViewDataChanged={tasksDataChanged[TEAM_TASKS_VIEW_TYPES.card]}
                    refreshData={refreshData}
                    employee={loggedInEmployee}
                    selectedTeam={team}
                    type={selectedTaskType}
                    isOpenCreateGoalPanel={isOpenCreateGoalPanel}
                    setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
                    isCreateNewTaskInProgress={isCreateNewTaskInProgress}
                    setIsCreateNewTaskInProgress={setIsCreateNewTaskInProgress}
                    page={page}
                    setPage={setPage}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    orderBy={orderBy}
                    order={order}
                    orderByDispatch={orderByDispatch}
                    loading={loading}
                    setLoading={setLoading}
                    totalTasks={totalTasks}
                    setTotalTasks={setTotalTasks}
                    tasks={isGoalOptionSelected ? goals : tasks}
                    tasksDispatch={isGoalOptionSelected ? goalsDispatch : tasksDispatch}
                    getData={getData}
                    {...duration}
                  />
                )}

                {TEAM_TASKS_VIEW_TYPES.card === teamTasksViewType && (
                  <TeamTasksCardView
                    setTeamCount={setTeamCount}
                    tagId={team.id}
                    tagType={SEARCH_TAG_TYPES.team}
                    style={cardViewStyles}
                    isSectionVisible={TEAM_TASKS_VIEW_TYPES.card === teamTasksViewType}
                    activeFilters={activeFilters}
                    updateTasksLoading={updateTasksLoading}
                    updateIsTasksDataChanged={updateIsCardViewDataChanged}
                    hasTableViewDataChanged={tasksDataChanged[TEAM_TASKS_VIEW_TYPES.list]}
                    refreshData={refreshData}
                    selectedTeam={team}
                    type={selectedTaskType}
                    {...duration}
                    showListView={showListView}
                    timeBoundPeriod={selectedTimeBoundPeriods?.[0] ?? null}
                    selectedCohort={sideNavSelectedCohort}
                  />
                )}
              </ViewsContainer>
            )}
          </>
        ) : (
          <Skeleton height="30vh" />
        )}
        <CustomBackdrop />
      </Container>
    )
  );
}

/**
 * @deprecated 06-08-2024
 */
const Team = () => (
  <GoalAnalyticsProvider>
    <TeamWithoutGoalAnalyticsProvider />
  </GoalAnalyticsProvider>
);
export default Team;

const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const CreateButton = styled(StyledButton)`
  padding: 8px 40px;
  margin-right: 2rem;
  border-color: ${(props) => props.theme.palette.primary.main};
`;

const ViewsContainer = styled.div`
  margin-top: 24px;
`;
const ColoredIconButton = styled(StyledIconButton)`
  position: relative;
  background: ${theme.palette.icons.white};
  border: 1px solid ${theme.palette.icons.blue};
  width: 24px;
  height: 24px;
`;
